import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { RichText, richTextHasContent, sanitizeRichText, toEditableHtml } from "@keydials/ui";
import { Icon } from "./Icon";

// The same six status hex values used across the app's palettes (web-admin/src/index.css --st-*),
// as literal hex so stored content renders identically regardless of the reader's theme.
const COLOR_SWATCHES = [
  { label: "Red", value: "#e11d48" },
  { label: "Amber", value: "#f59e0b" },
  { label: "Blue", value: "#3b82f6" },
  { label: "Teal", value: "#14b8a6" },
  { label: "Green", value: "#16a34a" },
  { label: "Grey", value: "#cbd5e1" },
];

// Safari has never shipped `hiliteColor`; `backColor` is its long-standing equivalent there.
function highlightCommand(): string {
  return typeof document.queryCommandSupported === "function" && document.queryCommandSupported("hiliteColor")
    ? "hiliteColor"
    : "backColor";
}

/** Someone who can be @mentioned from this editor (KEY-592). `hint` is a quiet second line, e.g. their role. */
export interface MentionCandidate {
  userId: string;
  name: string;
  hint?: string;
}

/** The "@query" being typed: which text node, where the "@" starts, where the caret is, and where to draw the list
 *  (viewport coordinates — the list is portalled, see below). */
interface MentionDraft {
  node: Text;
  start: number;
  end: number;
  query: string;
  top: number;
  left: number;
}

const MAX_MENTION_SUGGESTIONS = 8;

/**
 * Lightweight WYSIWYG editor (bold / italic / underline / lists / link). Produces sanitised HTML so the
 * formatting renders safely wherever RichText is used. The contentEditable surface is uncontrolled — seeded
 * from the model when not focused and never rewritten mid-edit (which would drop the caret) — and every
 * change is sanitised before it leaves the component.
 *
 * KEY-592 — @mentions. Given `mentionables`, typing "@" followed by part of a name offers the matching people; picking
 * one (click, Enter or Tab) swaps the "@query" for a mention token, `<span data-mention="{userId}">@Name</span>`,
 * which the server reads back out of the stored body to know who to notify. The token is one uneditable unit while
 * you type, so a half-deleted name can never leave the id pointing at someone the text no longer names.
 */
export function RichTextEditor({
  value,
  onChange,
  disabled,
  placeholder,
  minHeight = 64,
  mentionables,
}: {
  value: string | null | undefined;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  minHeight?: number;
  /** People who may be @mentioned here. Omitted or empty: typing "@" is just typing "@". */
  mentionables?: MentionCandidate[];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [mention, setMention] = useState<MentionDraft | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [empty, setEmpty] = useState(() => !richTextHasContent(value));
  const [openPicker, setOpenPicker] = useState<"color" | "highlight" | null>(null);

  useEffect(() => {
    if (!openPicker) {
      return;
    }
    const onDocMouseDown = (e: MouseEvent) => {
      if (!toolbarRef.current?.contains(e.target as Node)) {
        setOpenPicker(null);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [openPicker]);

  useEffect(() => {
    const el = ref.current;
    if (!el || focused) {
      return; // never overwrite the DOM while the user is editing
    }
    const incoming = toEditableHtml(value);
    if (el.innerHTML !== incoming) {
      el.innerHTML = incoming;
    }
    setEmpty(!richTextHasContent(value));
  }, [value, focused]);

  // Read-only: show the formatted content, not an editable box.
  if (disabled) {
    return richTextHasContent(value) ? <RichText value={value} /> : null;
  }

  const emit = () => {
    const el = ref.current;
    if (!el) {
      return;
    }
    setEmpty(!(el.textContent ?? "").trim());
    onChange(sanitizeRichText(el.innerHTML));
  };

  const suggestions = mention
    ? (mentionables ?? [])
        .filter((p) => p.name.toLowerCase().includes(mention.query.toLowerCase()))
        .slice(0, MAX_MENTION_SUGGESTIONS)
    : [];

  // Is the caret sitting just after "@something" in plain text? If so, remember where, for the suggestion list.
  const detectMention = () => {
    const sel = window.getSelection();
    const node = sel?.anchorNode;
    if (!mentionables?.length || !sel || !sel.isCollapsed || !node || node.nodeType !== Node.TEXT_NODE
      || !ref.current?.contains(node) || node.parentElement?.closest("[data-mention]")) {
      setMention(null);
      return;
    }
    const before = (node.textContent ?? "").slice(0, sel.anchorOffset);
    const match = /(?:^|[\s\u00a0(])@([^\s\u00a0@]{0,40})$/.exec(before);
    if (!match) {
      setMention(null);
      return;
    }
    const start = sel.anchorOffset - match[1].length - 1;
    const range = document.createRange();
    range.setStart(node, start);
    range.setEnd(node, sel.anchorOffset);
    // Not every environment lays text out (jsdom has no Range geometry): fall back to the editor's own box.
    const at = typeof range.getBoundingClientRect === "function"
      ? range.getBoundingClientRect()
      : wrapRef.current?.getBoundingClientRect();
    if (mention?.query !== match[1]) {
      setActiveIndex(0); // a new query is a new list: start from its top
    }
    setMention({
      node: node as Text,
      start,
      end: sel.anchorOffset,
      query: match[1],
      top: at ? at.bottom + 4 : 0,
      left: at ? Math.max(8, Math.min(at.left, window.innerWidth - 288)) : 0,
    });
  };

  const pickMention = (person: MentionCandidate) => {
    if (!mention || !mention.node.isConnected) {
      setMention(null);
      return;
    }
    const range = document.createRange();
    range.setStart(mention.node, mention.start);
    range.setEnd(mention.node, Math.min(mention.end, mention.node.length));
    range.deleteContents();
    const token = document.createElement("span");
    token.setAttribute("data-mention", person.userId);
    token.className = "kd-mention";
    token.contentEditable = "false";
    token.textContent = `@${person.name}`;
    // A non-breaking space after the token, so the caret has somewhere to land that is not inside it.
    const gap = document.createTextNode("\u00a0");
    range.insertNode(gap);
    range.insertNode(token);
    const caret = document.createRange();
    caret.setStart(gap, 1);
    caret.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(caret);
    setMention(null);
    emit();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!mention) {
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setMention(null);
      return;
    }
    if (suggestions.length === 0) {
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((i) => (i + step + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      pickMention(suggestions[Math.min(activeIndex, suggestions.length - 1)]);
    }
  };

  const exec = (command: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand("styleWithCSS", false, "false"); // prefer tags (<b>) over inline styles
    document.execCommand(command, false, arg);
    emit();
  };

  // Colour/highlight must land as an inline style (styleWithCSS true), not a legacy <font> tag —
  // only the sanitizer's SPAN/style allow-list understands that shape.
  const execStyled = (command: string, arg: string) => {
    ref.current?.focus();
    document.execCommand("styleWithCSS", false, "true");
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Link URL", "https://");
    if (url && url.trim()) {
      exec("createLink", url.trim());
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    // Paste as plain text — keeps out bloated/unsafe markup from Word, web pages, etc.
    e.preventDefault();
    document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
  };

  // A toolbar button. mousedown is prevented so clicking it never steals the editor's selection.
  const tool = (title: string, run: () => void, content: ReactNode) => (
    <button key={title} type="button" className="kd-rte-btn" title={title} onMouseDown={(e) => e.preventDefault()} onClick={run}>
      {content}
    </button>
  );

  // A toolbar button that opens a small swatch popover instead of running a command directly.
  const picker = (title: string, kind: "color" | "highlight", content: ReactNode) => (
    <div className="kd-rte-picker" key={title}>
      <button
        type="button"
        className="kd-rte-btn"
        title={title}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpenPicker((p) => (p === kind ? null : kind))}
      >
        {content}
      </button>
      {openPicker === kind && (
        <div className="kd-rte-swatches">
          {COLOR_SWATCHES.map((s) => (
            <button
              key={s.value}
              type="button"
              className="kd-rte-swatch"
              style={{ background: s.value }}
              title={s.label}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                execStyled(kind === "color" ? "foreColor" : highlightCommand(), s.value);
                setOpenPicker(null);
              }}
            />
          ))}
          <button
            type="button"
            className="kd-rte-swatch kd-rte-swatch-clear"
            title={kind === "color" ? "Clear text colour" : "Clear highlight"}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              execStyled(kind === "color" ? "foreColor" : highlightCommand(), kind === "color" ? "inherit" : "transparent");
              setOpenPicker(null);
            }}
          >
            <Icon name="ban" size={12} />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={"kd-rte" + (focused ? " focused" : "")}>
      <div className="kd-rte-toolbar" ref={toolbarRef}>
        {tool("Bold", () => exec("bold"), <span style={{ fontWeight: 800 }}>B</span>)}
        {tool("Italic", () => exec("italic"), <span style={{ fontStyle: "italic", fontFamily: "Georgia, serif" }}>I</span>)}
        {tool("Underline", () => exec("underline"), <span style={{ textDecoration: "underline" }}>U</span>)}
        <span className="kd-rte-sep" />
        {tool("Bulleted list", () => exec("insertUnorderedList"), <Icon name="list" size={14} />)}
        {tool("Numbered list", () => exec("insertOrderedList"), <Icon name="listOrdered" size={14} />)}
        {tool("Link", addLink, <Icon name="link" size={14} />)}
        <span className="kd-rte-sep" />
        {picker("Text colour", "color", <Icon name="palette" size={14} />)}
        {picker("Highlight", "highlight", <Icon name="highlighter" size={14} />)}
        <span className="kd-rte-sep" />
        {tool("Clear formatting", () => exec("removeFormat"), <Icon name="ban" size={13} />)}
      </div>
      <div className="kd-rte-wrap" ref={wrapRef}>
        <div
          ref={ref}
          className="kd-rte-area kd-richtext"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder}
          aria-autocomplete={mentionables?.length ? "list" : undefined}
          aria-expanded={mentionables?.length ? mention !== null : undefined}
          style={{ minHeight }}
          onInput={() => {
            emit();
            detectMention();
          }}
          onKeyDown={onKeyDown}
          onKeyUp={(e) => {
            // Moving the caret by keyboard can walk into or out of an "@query" without any input.
            if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
              detectMention();
            }
          }}
          onClick={detectMention}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setMention(null);
            emit();
          }}
          onPaste={onPaste}
        />
        {empty && placeholder && <div className="kd-rte-placeholder">{placeholder}</div>}
        {/* Portalled with fixed positioning, as AnchoredMenu is (KEY-532): the editor's frame clips its overflow,
            and so does the scrolling card stack it sits in, so a list drawn inside either is cut off. */}
        {mention && createPortal(
          <div className="kd-rte-mentions" role="listbox" aria-label="People to mention" style={{ position: "fixed", top: mention.top, left: mention.left }}>
            {suggestions.length === 0 ? (
              <div className="kd-rte-mention-empty">Nobody who can see this matches “@{mention.query}”</div>
            ) : (
              suggestions.map((p, i) => (
                <button
                  key={p.userId}
                  type="button"
                  role="option"
                  aria-selected={i === activeIndex}
                  className={"kd-rte-mention-option" + (i === activeIndex ? " active" : "")}
                  // mousedown would move focus off the editor and lose the caret the pick is anchored to.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => pickMention(p)}
                >
                  <span className="kd-rte-mention-name">{p.name}</span>
                  {p.hint && <span className="kd-rte-mention-hint">{p.hint}</span>}
                </button>
              ))
            )}
          </div>,
          document.body,
        )}
      </div>
    </div>
  );
}
